import { convertErrorForLogging, logError } from "@homeinstead/shared/logging";
import { apiGet } from "@homeinstead/shared/outbound";
import {
  getMarketingApiConfig,
  MARKETING_API_KEY_HEADER,
} from "./get-marketing-api-config";

type MarketingApiGetOptions<T> = {
  /** Path and query string, e.g. `/api/v1/jobs/search?searchQuery=foo` */
  path?: string;
  /** Full request URL; use instead of `path` when the URL is built elsewhere */
  url?: string;
  /** Returned when the request fails */
  emptyValue: T;
  logMessage: string;
  logContext?: Record<string, unknown>;
  logTags?: Record<string, string>;
  /** Transform a successful response; return `emptyValue` to treat as empty */
  mapResponse?: (response: unknown) => T;
  /** When true, rethrow after logging instead of returning `emptyValue` */
  throwOnError?: boolean;
};

function buildMarketingApiUrl(path: string): string {
  const { url } = getMarketingApiConfig();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${url.replace(/\/$/, "")}${normalizedPath}`;
}

async function marketingApiGet<T>(
  options: MarketingApiGetOptions<T>,
): Promise<T> {
  const {
    emptyValue,
    logContext = {},
    logMessage,
    logTags,
    mapResponse,
    path,
    throwOnError = false,
    url: requestUrlOption,
  } = options;

  const { apiKey } = getMarketingApiConfig();
  const requestUrl = requestUrlOption ?? buildMarketingApiUrl(path ?? "");

  try {
    const raw = await apiGet<unknown>(
      requestUrl,
      {
        headers: {
          [MARKETING_API_KEY_HEADER]: apiKey,
        },
      },
      {},
    );

    if (mapResponse) {
      return mapResponse(raw);
    }

    return raw as T;
  } catch (error) {
    logError({
      data: {
        error: convertErrorForLogging(error),
        ...logContext,
      },
      ...(logTags
        ? {
            details: {
              tags: logTags,
            },
          }
        : {}),
      message: logMessage,
    });
    if (throwOnError) {
      throw error;
    }
    return emptyValue;
  }
}

export { buildMarketingApiUrl, marketingApiGet };
